import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router';

import config from '../../../config';
import SectionShareModal from '../../../containers/ListingPage/SectionShareModal';
import { hasOpenBookingLimit } from '../../../util/annualPass';
import useAppSelector from '../../../util/hooks/useAppSelector';
import usePass from '../../../util/hooks/usePass';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { LISTING_STATE_CLOSED } from '../../../util/types';
import { parse } from '../../../util/urlHelpers';
import DesktopBookingPanel from './DesktopBookingPanel';
import MobileBookingPanel from './MobileBookingPanel';

// Parse ?checkIn=YYYY-MM-DD&checkOut=YYYY-MM-DD into a [moment, moment] tuple
// that BookingDatesForm can consume. Returns null if either date is missing,
// malformed, or if checkOut is not after checkIn.
const parseInitialDatesFromSearch = (search) => {
  const { checkIn, checkOut } = parse(search);
  if (typeof checkIn !== 'string' || typeof checkOut !== 'string') return null;
  const start = moment(checkIn, 'YYYY-MM-DD', true);
  const end = moment(checkOut, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
  return [start, end];
};

const ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT = config.annualPass.simultaneouslyBookingLimit;

const baseBoxStyle = {
  right: 0,
  zIndex: 2,
  backgroundColor: 'white',
  boxShadow: '0px 2px 16px rgba(0,0,0,0.06)',
  border: '1px solid #EFEFEF',
};
const mobileBoxStyle = {
  ...baseBoxStyle,
  position: 'fixed',
  bottom: 0,
  left: 0,
  transition: 'padding-bottom 0.3s ease',
  paddingBottom: '100px',
};

const desktopBoxStyle = {
  ...baseBoxStyle,
  position: 'absolute',
  height: 'fit-content',
  top: 0,
  left: 0,
  right: 0,
  p: 3,
  borderRadius: 2,
};

const BookingPanelComponent = ({
  distanceToLocation,

  // Listing data and status
  currentListing,
  category,
  isOwnListing = false,

  // Pricing and booking details
  formattedPrice,
  priceTitle,
  bookingLimit = null,
  minimumNights = 1,
  voucher = null,
  voucherValidationInProgress = false,
  voucherValidationError = null,

  // User and booking information
  toManyTransactionsWithProvider = false,

  // Reviews and social interaction
  onShareListing,

  // Booking and listing management
  onSubmit,
  onFetchTransactionLineItems,

  // Timeslots and availability
  timeSlots = null,
  onFetchTimeSlots,
  fetchTimeSlotsInProgress = false,
  fetchTimeSlotsError = null,

  // Financial elements and transactions
  lineItems = [],
  customLineItemTranslations = {},
  fetchLineItemsInProgress = false,
  fetchLineItemsError = null,

  // Other UI elements and states
  title,
  openShareModal = false,
  getTranslation,

  // Scrolling Effect Target
  startTargetRef,
  scrollTargetRef,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const [passIsValid, pass] = usePass();
  const panelRef = useRef(null);
  const panelContainerRef = useRef(null);

  // URL deep-link support for the route planner's "Book now" CTA and any
  // other caller that wants to land users in the booking flow with dates
  // pre-filled. Parsed once per location.search change.
  const initialDates = useMemo(() => parseInitialDatesFromSearch(location.search), [location.search]);
  const autoOpenModal = useMemo(
    () => Boolean(parse(location.search).book),
    [location.search],
  );
  const { currentUserOpenOrdersCount, currentUserHasOrdersInProgress } = useAppSelector(
    (state) => ({
      currentUserOpenOrdersCount: state.user.currentUserOpenOrdersCount,
      currentUserHasOrdersInProgress: state.user.currentUserHasOrdersInProgress,
    }),
    shallowEqual
  );

  const { isMobile, isTablet } = useScreenSize();

  const toManyOpenOrdersAsAnnualPassUser =
    hasOpenBookingLimit(pass) &&
    passIsValid &&
    currentUserOpenOrdersCount >= ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT;
  const showAnnualPassError =
    process.env.REACT_APP_ENV === 'production' &&
    ((toManyOpenOrdersAsAnnualPassUser &&
      intl.formatMessage({ id: 'BookingPanel.toManyOpenOrdersAsAnnualPassUser' })) ||
      (toManyTransactionsWithProvider &&
        intl.formatMessage({ id: 'BookingPanel.toManyTransactionsWithProvider' })));

  const hasListingState = !!currentListing.attributes.state;
  const isClosed = hasListingState && currentListing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm = !isClosed;

  useEffect(() => {
    const topbarOffset = 72;
    const padding = 16;
    const sectionSeparator = 16;
    const marginBottom = 32;

    // Set the start values for the scroll effect
    let topOfStartTarget = startTargetRef?.current?.getBoundingClientRect().top + window.scrollY;
    let topOfScrollTarget = scrollTargetRef?.current?.getBoundingClientRect().top + window.scrollY;

    const repositionPanel = () => {
      if (panelRef.current && startTargetRef.current && scrollTargetRef.current) {
        const panelHeight = panelRef.current.offsetHeight;
        const currentScrollY = window.scrollY;
        // ---> ______
        //      |    |
        //      |    |

        // If the panel top is crossing the top of the section, make it fixed
        // going from top to bottom
        if (currentScrollY >= topOfStartTarget - topbarOffset) {
          panelRef.current.style.position = 'fixed';
          panelRef.current.style.top = `${topbarOffset + padding}px`;
          panelRef.current.style.width = `${startTargetRef.current.offsetWidth}px`;
          panelRef.current.style.left = `${startTargetRef.current.offsetLeft}px`;
        }

        // If the panel top is crossing the top of the section, make it absolute
        // going from bottom to top
        if (currentScrollY <= topOfStartTarget - topbarOffset) {
          panelRef.current.style.position = 'absolute';
          panelRef.current.style.width = '100%';
          panelRef.current.style.top = '0';
          panelRef.current.style.left = '0';
        }

        //      |    |
        //      |    |
        // ---> ------

        // If the panel bottom is crossing the bottom of the target section, we have to absolute the panel position there
        // going from top to bottom
        if (
          currentScrollY + panelHeight + topbarOffset + padding >=
          topOfScrollTarget - sectionSeparator - marginBottom
        ) {
          panelRef.current.style.position = 'absolute';
          panelRef.current.style.top = `${
            topOfScrollTarget -
            sectionSeparator -
            marginBottom -
            panelHeight -
            topOfStartTarget -
            padding
          }px`;
          panelRef.current.style.width = `${startTargetRef.current.offsetWidth}px`;
          panelRef.current.style.left = `0px`;
        }
      }
    };

    const handleResize = () => {
      // here the positions of the starting and ending targets are recalculated
      topOfStartTarget = startTargetRef.current.getBoundingClientRect().top + window.scrollY;
      topOfScrollTarget = scrollTargetRef.current.getBoundingClientRect().top + window.scrollY;
    };

    // check if the height of the panel is bigger than the viewport height
    // don't scroll it when it is

    if (panelRef.current) {
      const panelHeight = panelRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      if (panelHeight > viewportHeight) {
        return;
      }
    }

    window.addEventListener('scroll', repositionPanel);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', repositionPanel);
      window.removeEventListener('resize', handleResize);
    };
  }, [panelRef, startTargetRef, scrollTargetRef]);

  const mobilePanelRef = useRef(null);
  const [scrollDirection, setScrollDirection] = useState('up');

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      if (isTablet) {
        if (window.scrollY > lastScrollY) {
          setScrollDirection('down');
        } else {
          setScrollDirection('up');
        }
      }
      lastScrollY = window.scrollY <= 0 ? 0 : window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTablet]);

  return (
    <>
      {isTablet || isMobile ? (
        <Box
          px={2}
          py={2}
          ref={mobilePanelRef}
          sx={{
            ...mobileBoxStyle,
            ...{
              paddingBottom: scrollDirection === 'down' ? '44px' : '100px',
            },
          }}
        >
          <MobileBookingPanel
            autoOpenModal={autoOpenModal}
            bookingLimit={bookingLimit}
            category={category}
            currentListing={currentListing}
            currentUserHasOrdersInProgress={currentUserHasOrdersInProgress}
            customLineItemTranslations={customLineItemTranslations}
            distanceToLocation={distanceToLocation}
            fetchLineItemsError={fetchLineItemsError}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchTimeSlotsError={fetchTimeSlotsError}
            fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
            formattedPrice={formattedPrice}
            getTranslation={getTranslation}
            initialDates={initialDates}
            isClosed={isClosed}
            isOwnListing={isOwnListing}
            lineItems={lineItems}
            minimumNights={minimumNights}
            onFetchTimeSlots={onFetchTimeSlots}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onSubmit={onSubmit}
            priceTitle={priceTitle}
            showAnnualPassError={showAnnualPassError}
            showBookingDatesForm={showBookingDatesForm}
            timeSlots={timeSlots}
            title={title}
            voucher={voucher}
            voucherValidationError={voucherValidationError}
            voucherValidationInProgress={voucherValidationInProgress}
          />
        </Box>
      ) : (
        <Box
          ref={panelContainerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        >
          <Box ref={panelRef} sx={{ ...baseBoxStyle, ...desktopBoxStyle }}>
            <Typography color="text.secondary" mb={1} variant="body2">
              <FormattedMessage
                id="ListingPage.availableSeats.text"
                values={{ seats: currentListing.attributes.publicData.amountOfSeats || 1 }}
              />
            </Typography>
            <DesktopBookingPanel
              appliedVoucher={voucher}
              autoOpenModal={autoOpenModal}
              bookingLimit={bookingLimit}
              category={category}
              currentListing={currentListing}
              currentUserHasOrdersInProgress={currentUserHasOrdersInProgress}
              customLineItemTranslations={customLineItemTranslations}
              distanceToLocation={distanceToLocation}
              fetchLineItemsError={fetchLineItemsError}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchTimeSlotsError={fetchTimeSlotsError}
              fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
              formattedPrice={formattedPrice}
              getTranslation={getTranslation}
              initialDates={initialDates}
              isClosed={isClosed}
              isOwnListing={isOwnListing}
              lineItems={lineItems}
              minimumNights={minimumNights}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onSubmit={onSubmit}
              showAnnualPassError={showAnnualPassError}
              showBookingDatesForm={showBookingDatesForm}
              timeSlots={timeSlots}
              title={title}
              voucherValidationError={voucherValidationError}
              voucherValidationInProgress={voucherValidationInProgress}
            />
          </Box>
        </Box>
      )}

      <SectionShareModal
        currentListing={currentListing}
        onShareListing={onShareListing}
        open={openShareModal}
      />
    </>
  );
};

BookingPanelComponent.displayName = 'BookingPanel';

const BookingPanel = memo(BookingPanelComponent);

export default BookingPanel;
